import { Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { memo, useCallback, useRef } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { enqueueSnackbar } from "notistack";
import { DownloadExcelButton, ReportRunner, useDateFilter, useLandmarkFilter } from "../runner/report-runner";
import { formatDatetimeForPreview } from '../../../../utils/date-utils';
import { useAuthContext } from '../../../../auth/hooks';

type VehicleOdometerLine = {
  vehicleUnitNumber: string,
  vin: string,
  lastRecordedOdometer: number,
  lastRecordedEngineHours: number,
  lastRecordedAt: string,
}

type VehicleOdometerReportLinesProps = {
  lines?: VehicleOdometerLine[]
}

const VehicleOdometerReportLines = memo(({ lines }: VehicleOdometerReportLinesProps) => {
  const { sortedList, sortData } = useSort(lines);

  const { session } = useAuthContext();
  if (!sortedList) {
    return null;
  }

  const timezone = session?.organization?.companyTimezone as string;

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="vehicleUnitNumber" sortData={sortData}>
                Vehicle
              </SortableHeader>

              <SortableHeader sortKey="vin" sortData={sortData} >
                Vin
              </SortableHeader>

              <SortableHeader sortKey="lastRecordedOdometer" sortData={sortData} align="center">
                Last Recorded Odometer
              </SortableHeader>

              <SortableHeader sortKey="lastRecordedEngineHours" sortData={sortData} align="center">
                Last Recorded Engine Hours
              </SortableHeader>

              <SortableHeader sortKey="lastRecordedAt" sortData={sortData}>
                Last Recorded At
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((line, index) =>
              (<TableRow key={index}>
                  <TruxTableCell>
                    {line.vehicleUnitNumber}
                  </TruxTableCell>
                  <TruxTableCell>
                    {line.vin}
                  </TruxTableCell>
                  <TruxTableCell align="center">
                    {line.lastRecordedOdometer.toFixed(0)}
                  </TruxTableCell >
                  <TruxTableCell align="center">
                    {line.lastRecordedEngineHours}
                  </TruxTableCell>
                  <TruxTableCell>
                    {formatDatetimeForPreview(line.lastRecordedAt, timezone)}
                  </TruxTableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  lines?: VehicleOdometerLine[],
}

export default function Page() {
  const settings = useSettingsContext();
  const currentRequest = useRef({} as any);

  const dataLoader = useCallback(() => ({
    lines: (currentRequest.current as any).ran && axios.post(
      endpoints.reports.vehicleOdometer.data,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data.lines),
  }), []);
  const {
    data,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  const daysDormantFilter = useDateFilter();
  const landmarkFilter = useLandmarkFilter();

  const filters = [
    daysDormantFilter,
    landmarkFilter
  ];

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Odometer Per Vehicle">
          <DownloadExcelButton baseUrl={endpoints.reports.vehicleOdometer.excel} filters={filters} currentRequest={currentRequest} />
        </PageHeader>

        <ReportRunner
          filters={filters}
          onValidate={(errorMessage) => enqueueSnackbar(errorMessage, {variant: "error"})}
          onRun={(request) => {
            currentRequest.current = request;
            setShouldRefresh(true);
          }}
          loading={loading}
        />

        {data && (data.lines?.length || 0) > 0 && <VehicleOdometerReportLines lines={data.lines || []} />}
        {loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container >
  );
}
